import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import { TilePainel } from '@/pages/gerenciar/components/TilePainel';

interface Props {
  eventoId: number;
}

interface PortariaDados {
  ingressos_validos: number;
  ja_entraram: number;
  ainda_nao_chegaram: number;
  pendentes_de_pagamento: number;
  ultima_entrada: string | null;
}

function formatarHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${min}`;
}

export default function AbaPortaria({ eventoId }: Props) {
  const navigate = useNavigate();
  const [dados, setDados] = useState<PortariaDados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setCarregando(true);
      setErro(null);
      const { data, error } = await supabase
        .from('vw_portaria')
        .select(
          'ingressos_validos, ja_entraram, ainda_nao_chegaram, pendentes_de_pagamento, ultima_entrada',
        )
        .eq('evento_id', eventoId)
        .maybeSingle();
      if (!ativo) return;
      if (error) {
        setErro(mensagemAmigavel(error));
        setCarregando(false);
        return;
      }
      setDados((data as PortariaDados) ?? null);
      setCarregando(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  if (carregando) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 esqueleto rounded-3xl bg-secondary-100" />
        ))}
      </div>
    );
  }

  if (erro) {
    return (
      <div className="rounded-3xl bg-white p-10 text-center shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  const tiles = [
    { rotulo: 'Válidos', valor: dados?.ingressos_validos ?? 0, icone: 'ri-ticket-2-line', tom: 'primary' as const },
    { rotulo: 'Já entraram', valor: dados?.ja_entraram ?? 0, icone: 'ri-check-double-line', tom: 'success' as const },
    {
      rotulo: 'Ainda não chegaram',
      valor: dados?.ainda_nao_chegaram ?? 0,
      icone: 'ri-user-line',
      tom: 'warning' as const,
    },
  ];

  return (
    <div className="space-y-4">
      <DicaPrimeiraVez id="painel.portaria.primeira" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {tiles.map((t) => (
          <TilePainel
            key={t.rotulo}
            icone={t.icone}
            tom={t.tom}
            valor={Number(t.valor).toLocaleString('pt-BR')}
            legenda={t.rotulo}
          />
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-foreground-900">Última entrada</p>
          <p className="mt-0.5 text-[13px] text-foreground-500">
            {formatarHora(dados?.ultima_entrada ?? null)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/portaria/${eventoId}`)}
          className="flex min-h-[52px] shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-5 text-[15px] font-bold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-camera-line" />
          </span>
          Abrir leitor
        </button>
      </div>
    </div>
  );
}